import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Storefront, Plus, Sparkle, ShoppingCart } from '@phosphor-icons/react';
import { useAuth } from '../auth/AuthContext';
import CartPanel from '../components/CartPanel';
import SmartSaverPlannerModal from '../components/SmartSaverPlannerModal';
import AlertDialog from '../components/AlertDialog';
import { customerUnitPrice, isDefaultWholesalePrice, sortFlavours } from '../lib/bundleHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Catalogue grouping: keep the cart-side flow predictable by sorting categories
// by name and putting un-categorised products under a stable bucket.
const UNCATEGORISED_KEY = 'Other';

// Resolve the SKU for the currently-selected flavour so we can point the
// product image at `/product-images/by-sku/{sku}/file`. Flavourless products
// store the SKU under the empty-string key in `flavourCodes`.
const resolveSku = (product, flavour) => {
  const codes = product.flavourCodes || {};
  if (flavour && codes[flavour]) return codes[flavour];
  const values = Object.values(codes).filter(Boolean);
  if (values.length === 1) return values[0];
  return null;
};

// Single product card in the catalogue grid. Handles flavour selection (if any)
// and adding the item to the cart via the parent callback.
const CatalogueCard = ({ product, onAdd, busy, isCustomer, hideWholesalePrice, showSaverBadge = true }) => {
  const flavours = useMemo(() => sortFlavours(product.flavours || []), [product.flavours]);
  const [flavour, setFlavour] = useState(flavours[0] || '');
  const [qty, setQty] = useState(1);
  const unit = product.customerUnitPrice; // injected from parent (computed per customer)

  const disabled = busy || qty < 1 || (flavours.length > 0 && !flavour);

  // Image URL priority: uploaded image matched by SKU → legacy imageUrl → none.
  // We track load failures so falling back to the placeholder is instant.
  const sku = resolveSku(product, flavour);
  const skuImgUrl = sku ? `${API}/product-images/by-sku/${encodeURIComponent(sku)}/file` : null;
  const [skuImgFailed, setSkuImgFailed] = useState(false);
  // Reset the "failed" flag whenever the SKU changes (i.e. flavour switch).
  useEffect(() => { setSkuImgFailed(false); }, [sku]);
  const imgSrc = (!skuImgFailed && skuImgUrl) ? skuImgUrl : (product.imageUrl || null);

  return (
    <div
      className="bg-surface border border-border-color rounded-xl p-4 flex flex-col gap-3"
      data-testid={`browse-card-${product.id}`}
    >
      <div className="flex items-start gap-3">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={product.name}
            className="h-16 w-16 object-cover rounded border border-border-color shrink-0"
            data-testid={`browse-card-img-${product.id}`}
            onError={(e) => {
              // If the SKU-based URL 404s (no upload yet), silently fall through
              // to the legacy `imageUrl` or the placeholder icon.
              if (!skuImgFailed && skuImgUrl && e.target.src.includes('/product-images/by-sku/')) {
                setSkuImgFailed(true);
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        ) : (
          <div className="h-16 w-16 rounded border border-border-color bg-black/40 shrink-0 flex items-center justify-center">
            <Storefront size={20} weight="duotone" className="text-text-secondary/40" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-text-primary leading-tight">{product.name}</h3>
            {showSaverBadge && product.isBundleable !== false && (
              <span
                className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-gold/10 text-gold border border-gold/30 rounded"
                title="Eligible for Smart Saver"
                data-testid={`browse-saver-badge-${product.id}`}
              >
                <Sparkle size={10} weight="fill" />
                Saver
              </span>
            )}
          </div>
          {product.size && <p className="text-[11px] text-text-secondary">{product.size}</p>}
          {product.description && (
            <p className="text-[11px] text-text-secondary mt-1 line-clamp-2" title={product.description}>
              {product.description}
            </p>
          )}
        </div>
      </div>

      <div className="text-xs text-text-secondary flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span>RRP ${Number(product.rrp).toFixed(2)}</span>
        {hideWholesalePrice ? (
          <span className="text-gold italic" data-testid={`browse-inquire-${product.id}`}>(inquire for wholesale price)</span>
        ) : (
          <>
            <span className="text-success font-semibold">W/sale ${unit.toFixed(2)}</span>
            {isCustomer && product.isDefaultPrice && (
              <span className="text-text-secondary/70 italic" data-testid={`browse-inquire-${product.id}`}>(inquire for wholesale price)</span>
            )}
          </>
        )}
      </div>

      {flavours.length > 0 && (
        <select
          value={flavour}
          onChange={(e) => setFlavour(e.target.value)}
          data-testid={`browse-flavour-${product.id}`}
          className="w-full px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs focus:border-gold focus:outline-none"
        >
          {flavours.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      )}

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            disabled={busy}
            className="w-7 h-7 rounded border border-border-color text-text-primary hover:border-gold text-sm disabled:opacity-30"
            data-testid={`browse-qty-minus-${product.id}`}
            aria-label="Decrease quantity"
          >−</button>
          <input
            type="number"
            min="1"
            value={qty}
            onChange={(e) => setQty(Math.min(999, Math.max(1, Math.floor(Number(e.target.value) || 1))))}
            disabled={busy}
            data-testid={`browse-qty-${product.id}`}
            aria-label="Quantity"
            className="w-12 bg-white border border-gray-400 rounded text-center text-xs font-bold text-black py-1 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            type="button"
            onClick={() => setQty((q) => q + 1)}
            disabled={busy}
            className="w-7 h-7 rounded border border-border-color text-text-primary hover:border-gold text-sm disabled:opacity-30"
            data-testid={`browse-qty-plus-${product.id}`}
            aria-label="Increase quantity"
          >+</button>
        </div>
        <button
          type="button"
          onClick={() => onAdd(product, flavours.length > 0 ? flavour : null, qty)}
          disabled={disabled}
          data-testid={`browse-add-${product.id}`}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gold text-black text-xs font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50"
        >
          <Plus size={14} weight="bold" />
          Add to cart
        </button>
      </div>
    </div>
  );
};

export const BrowseOrderPage = () => {
  const { user: me } = useAuth();
  const [allCustomers, setAllCustomers] = useState([]);
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  // Smart Saver references are hidden whenever the CONTEXT customer has the
  // override off — the customer themselves, or the admin/rep's selected context.
  const contextCustomer = me?.role === 'customer'
    ? me
    : allCustomers.find((c) => c.id === activeCustomerId);
  const ssDisabled = !!contextCustomer && contextCustomer.smartSaverEnabled === false;
  const [products, setProducts] = useState([]);
  const [productDiscounts, setProductDiscounts] = useState({});
  const [cart, setCart] = useState(null);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCart, setLoadingCart] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);

  const targetCustomerId = useMemo(() => {
    if (!me) return null;
    return me.role === 'customer' ? me.id : activeCustomerId;
  }, [me, activeCustomerId]);

  const fetchCustomers = useCallback(async () => {
    if (!me || me.role === 'customer') return;
    try {
      const res = await axios.get(`${API}/users`);
      setAllCustomers(res.data.filter((u) => u.role === 'customer' && u.active));
    } catch (e) {
      console.error('Error fetching customers:', e);
    }
  }, [me]);

  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const res = await axios.get(`${API}/products?active_only=true`);
      setProducts(res.data);
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const fetchEffectiveConfig = useCallback(async () => {
    if (!targetCustomerId) {
      setProductDiscounts({});
      return;
    }
    try {
      const res = await axios.get(`${API}/users/${targetCustomerId}/effective-config`);
      setProductDiscounts(res.data.productDiscounts || {});
    } catch (e) {
      console.error('Error fetching effective config:', e);
    }
  }, [targetCustomerId]);

  const fetchCart = useCallback(async () => {
    if (!targetCustomerId) {
      setCart(null);
      setLoadingCart(false);
      return;
    }
    setLoadingCart(true);
    try {
      const res = await axios.get(`${API}/cart?customerId=${targetCustomerId}`);
      setCart(res.data);
    } catch (e) {
      console.error('Error fetching cart:', e);
    } finally {
      setLoadingCart(false);
    }
  }, [targetCustomerId]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => {
    fetchEffectiveConfig();
    fetchCart();
  }, [fetchEffectiveConfig, fetchCart]);

  // Visible products: apply search filter only (Browse & Order shows EVERY active product
  // to the customer; the `hiddenProductIds` override is intentionally NOT applied here —
  // it scopes to the Smart Saver builder only). Compute customer price.
  const visibleProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products
      .filter((p) => {
        if (!q) return true;
        return [p.name, p.range, p.category, p.size, p.description]
          .map((v) => String(v || '').toLowerCase())
          .some((v) => v.includes(q));
      })
      .map((p) => {
        const isDefault = isDefaultWholesalePrice(p, productDiscounts);
        return {
          ...p,
          customerUnitPrice: customerUnitPrice(p, productDiscounts),
          // Tracks whether this product's wholesale price is the global 25% fallback
          // (no per-product override). Drives the "(inquire for wholesale price)"
          // caption and, for customer-role only, hides the numeric price entirely.
          isDefaultPrice: isDefault,
          hideWholesalePrice: me?.role === 'customer' && isDefault,
        };
      });
  }, [products, productDiscounts, search]);

  // Explicit category order for the catalogue display. Anything that doesn't match
  // (including the "Other" bucket for products with no category) is appended after
  // the listed categories, alphabetically.
  const CATEGORY_ORDER = useMemo(
    () => ['Protein', 'Amino Blends', 'Other Protein', 'Pure Series', 'Hydrate', 'Tablets'],
    [],
  );

  // Group by category (label-only, doesn't affect cart semantics).
  const productsByCategory = useMemo(() => {
    // Family-aware sort for products WITHIN a category. PRO75 first (1kg → 2kg),
    // ISO90 next (900g → 1.8kg), then every other family alphabetically by name.
    // Variants of the same family sort by size ascending so e.g. 1kg always
    // appears before 2kg. (Feb 26 2026 Raisey's hierarchy choice.)
    const FAMILY_ORDER = ['PRO75', 'ISO90'];
    const sizeToGrams = (sz) => {
      if (!sz) return 0;
      const m = String(sz).match(/^([\d.]+)\s*(kg|g)$/i);
      if (!m) return 0;
      return parseFloat(m[1]) * (m[2].toLowerCase() === 'kg' ? 1000 : 1);
    };
    const familyOf = (p) => (p.size ? p.name.replace(new RegExp(`\\s+${p.size.replace('.', '\\.')}$`), '').trim() : p.name);
    const familyRank = (fam) => {
      const i = FAMILY_ORDER.indexOf(fam);
      return i === -1 ? FAMILY_ORDER.length + 1 : i;
    };
    const sortItems = (items) => items.slice().sort((a, b) => {
      const fa = familyOf(a);
      const fb = familyOf(b);
      const ra = familyRank(fa);
      const rb = familyRank(fb);
      if (ra !== rb) return ra - rb;
      if (fa !== fb) return fa.localeCompare(fb);
      return sizeToGrams(a.size) - sizeToGrams(b.size);
    });

    const groups = new Map();
    for (const p of visibleProducts) {
      const key = (p.category && p.category.trim()) || UNCATEGORISED_KEY;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    const idx = (name) => {
      const i = CATEGORY_ORDER.indexOf(name);
      return i === -1 ? Number.POSITIVE_INFINITY : i;
    };
    return Array.from(groups.entries())
      .map(([k, items]) => [k, sortItems(items)])
      .sort(([a], [b]) => {
        const ai = idx(a);
        const bi = idx(b);
        if (ai !== bi) return ai - bi;
        // Both unlisted (or both at same explicit position — won't happen). Push the
        // "Other" bucket to the very end, otherwise sort alphabetically.
        if (a === UNCATEGORISED_KEY) return 1;
        if (b === UNCATEGORISED_KEY) return -1;
        return a.localeCompare(b);
      });
  }, [visibleProducts, CATEGORY_ORDER]);

  const handleAdd = async (product, flavour, qty) => {
    if (!targetCustomerId) {
      setAlertMsg({ title: 'Pick a customer first', body: 'Select a customer to build their cart.' });
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/cart/loose-items`, {
        customerId: targetCustomerId,
        productId: product.id,
        selectedFlavour: flavour || null,
        qtyDelta: qty,
      });
      setCart(res.data);
    } catch (e) {
      setAlertMsg({ title: 'Could not add item', body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateLooseQty = async (lineId, qty) => {
    if (!targetCustomerId) return;
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/cart/loose-items/${lineId}?customerId=${targetCustomerId}`,
        { qty },
      );
      setCart(res.data);
    } catch (e) {
      setAlertMsg({ title: 'Update failed', body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveLooseItem = async (lineId) => {
    if (!targetCustomerId) return;
    setBusy(true);
    try {
      const res = await axios.delete(
        `${API}/cart/loose-items/${lineId}?customerId=${targetCustomerId}`,
      );
      setCart(res.data);
    } catch (e) {
      setAlertMsg({ title: 'Remove failed', body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveSaver = async (bundleId) => {
    if (!targetCustomerId) return;
    setBusy(true);
    try {
      const res = await axios.delete(
        `${API}/cart/savers/${bundleId}?customerId=${targetCustomerId}`,
      );
      setCart(res.data);
    } catch (e) {
      setAlertMsg({ title: 'Remove Saver failed', body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleClearCart = async () => {
    if (!targetCustomerId) return;
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/cart?customerId=${targetCustomerId}`);
      setCart(res.data);
    } catch (e) {
      setAlertMsg({ title: 'Clear cart failed', body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async ({ prepayRequested = false, customerReference = '' } = {}) => {
    if (!targetCustomerId) return false;
    setBusy(true);
    setSubmitStatus(null);
    try {
      const flag = prepayRequested ? '&prepayRequested=true' : '';
      const ref = (customerReference || '').trim();
      const refParam = ref ? `&customerReference=${encodeURIComponent(ref)}` : '';
      const res = await axios.post(`${API}/cart/submit?customerId=${targetCustomerId}&acceptedTerms=true${flag}${refParam}`);
      setSubmitStatus({
        kind: 'ok',
        text: `Order sent · ${res.data.bundles_submitted} saver(s) + ${res.data.loose_items_submitted} loose unit(s)`,
      });
      await fetchCart();
      setTimeout(() => setSubmitStatus(null), 6000);
      return true;
    } catch (e) {
      setSubmitStatus({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Submit failed' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-4xl font-bold font-heading text-text-primary mb-2 flex items-center gap-3"
              data-testid="browse-page-title"
            >
              <Storefront size={32} weight="duotone" className="text-gold" />
              Browse &amp; Order
            </h1>
            <p className="text-text-secondary">
              {ssDisabled
                ? 'Add any catalogue item to the cart at standard wholesale.'
                : 'Add any catalogue item to the cart at standard wholesale, or stack it with a Smart Saver.'}
            </p>
          </div>
          {me && me.role !== 'customer' && (
            <div className="flex flex-col shrink-0" data-testid="browse-customer-selector-wrap">
              <label className="text-xs text-text-secondary mb-1">Building for</label>
              <select
                value={activeCustomerId || ''}
                onChange={(e) => setActiveCustomerId(e.target.value || null)}
                data-testid="browse-customer-selector"
                className="px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none min-w-[220px]"
              >
                <option value="">— Select a customer —</option>
                {allCustomers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div>
            <div className="mb-4">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="browse-search-input"
                placeholder="Search products by name, range, category, size…"
                className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary placeholder:text-text-secondary focus:border-gold focus:outline-none text-sm"
              />
            </div>

            {loadingProducts && <p className="text-text-secondary italic">Loading catalogue…</p>}

            {!loadingProducts && productsByCategory.length === 0 && (
              <p className="text-text-secondary italic" data-testid="browse-no-products">
                No products match your search.
              </p>
            )}

            <div className="space-y-8">
              {productsByCategory.map(([category, items]) => (
                <section key={category} data-testid={`browse-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <h2 className="text-lg font-bold font-heading text-gold mb-3 flex items-center gap-2">
                    {category}
                    <span className="text-xs text-text-secondary font-normal">({items.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((p) => (
                      <CatalogueCard
                        key={p.id}
                        product={p}
                        onAdd={handleAdd}
                        busy={busy}
                        isCustomer={me?.role === 'customer'}
                        hideWholesalePrice={p.hideWholesalePrice}
                        showSaverBadge={!ssDisabled}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>

          {targetCustomerId ? (
            <CartPanel
              cart={cart}
              loading={loadingCart}
              onUpdateLooseQty={handleUpdateLooseQty}
              onRemoveLooseItem={handleRemoveLooseItem}
              onRemoveSaver={handleRemoveSaver}
              onSubmit={handleSubmit}
              onClear={handleClearCart}
              onApplySmartSaver={ssDisabled ? undefined : () => setPlannerOpen(true)}
              isCustomer={me?.role === 'customer'}
              smartSaverHidden={ssDisabled}
              busy={busy}
              submitStatus={submitStatus}
            />
          ) : (
            <div
              className="bg-surface border border-border-color rounded-xl p-5 sticky top-6"
              data-testid="browse-cart-placeholder"
            >
              <h2 className="text-lg font-bold font-heading text-gold mb-2 flex items-center gap-2">
                <ShoppingCart size={20} weight="duotone" />
                Cart
              </h2>
              <p className="text-sm text-text-secondary italic">
                Pick a customer above to start building their cart. Prices shown below are
                the default wholesale &mdash; per-customer overrides apply once a customer is selected.
              </p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!alertMsg}
        title={alertMsg?.title || ''}
        message={alertMsg?.body || ''}
        variant="warning"
        onClose={() => setAlertMsg(null)}
      />
      <SmartSaverPlannerModal
        open={plannerOpen}
        customerId={targetCustomerId}
        onClose={() => setPlannerOpen(false)}
        onApplied={() => { fetchCart(); }}
      />
      {me?.role !== 'customer' && targetCustomerId && (
        <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2">
          <select
            disabled={sampleLoading || !targetCustomerId}
            onChange={async (e) => {
              const sample = e.target.value;
              e.target.value = '';
              if (!sample) return;
              setSampleLoading(true);
              try {
                await axios.post(`${API}/cart/load-sample/${sample}?customerId=${targetCustomerId}`);
                await fetchCart();
              } catch (err) {
                setAlertMsg({ title: 'Sample load failed',
                              body: err?.response?.data?.detail || err.message });
              } finally {
                setSampleLoading(false);
              }
            }}
            data-testid="sample-cart-loader"
            className="px-3 py-2 text-xs bg-surface border border-border-color rounded-lg text-text-secondary hover:border-gold focus:outline-none focus:border-gold disabled:opacity-50"
            title="Load a preset sample cart to test the Smart Saver planner (admin only)"
          >
            <option value="">🧪 Load sample cart…</option>
            <option value="small_mixed">Small mixed (1 small saver)</option>
            <option value="large_eligible">Large eligible (1 large saver)</option>
            <option value="leftover_demo">Leftover demo (1 saver + 1 suggestion)</option>
            <option value="box8_candidate">Box-of-8 candidate (8 Cores 1kg)</option>
            <option value="box6_candidate">Box-of-6 candidate (6 Cores 2kg)</option>
          </select>
          <input
            type="file"
            accept=".csv,text/csv"
            id="cart-upload-csv-input"
            data-testid="cart-upload-csv-input"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setSampleLoading(true);
              try {
                const fd = new FormData();
                fd.append('file', file);
                const res = await axios.post(
                  `${API}/cart/upload-csv?customerId=${targetCustomerId}`,
                  fd,
                  { headers: { 'Content-Type': 'multipart/form-data' } },
                );
                await fetchCart();
                const { matched_lines, matched_units, unmatched_count, unmatched } = res.data || {};
                const lines = [
                  `Matched ${matched_lines} product line(s) — ${matched_units} unit(s) loaded into cart.`,
                ];
                if (unmatched_count > 0) {
                  lines.push('');
                  lines.push(`${unmatched_count} unmatched row(s) (no catalogue SKU):`);
                  (unmatched || []).forEach((u) => {
                    const desc = u.description ? ` — ${u.description}` : '';
                    lines.push(`  • ${u.sku} (qty ${u.qty})${desc}`);
                  });
                  lines.push('');
                  lines.push('Fix these SKUs in the CSV or in Admin → Products → flavour codes, then re-upload.');
                }
                setAlertMsg({
                  title: unmatched_count > 0 ? 'CSV loaded with warnings' : 'CSV loaded',
                  body: lines.join('\n'),
                });
              } catch (err) {
                setAlertMsg({
                  title: 'CSV upload failed',
                  body: err?.response?.data?.detail || err.message,
                });
              } finally {
                setSampleLoading(false);
              }
            }}
          />
          <button
            type="button"
            disabled={sampleLoading || !targetCustomerId}
            onClick={() => document.getElementById('cart-upload-csv-input')?.click()}
            data-testid="cart-upload-csv-button"
            className="px-3 py-2 text-xs bg-surface border border-border-color rounded-lg text-text-secondary hover:border-gold focus:outline-none focus:border-gold disabled:opacity-50"
            title="Upload a real sales-order CSV (expects 'code' and 'qty_invoiced' columns) to test the planner against historical data"
          >
            📄 Upload real order CSV…
          </button>
        </div>
      )}
    </div>
  );
};

export default BrowseOrderPage;
